import {
  differenceInDays,
  differenceInMonths,
  differenceInYears
} from 'date-fns'
import { FormEvent } from 'react'
import { AnyObject } from 'typings/custom'
import errorMessages from 'helpers/errorMessages'
import { accountNumberConfig } from 'configureBrand'

const {
  deficitInvoiceTotalErrorMessage,
  exceedInvoiceTotalErrorMessage,
  invalidDecimalMessage,
  invalidMonthDuration,
  invalidYearDuration,
  isRequired,
  monthDifferenceErrorMessage,
  exceedCurrentMonth
} = errorMessages

export const isValidZipCode = (value: string): boolean => {
  // only numbers allowed
  const regExp = /^[0-9]+$/
  return regExp.test(value)
}

export const validAmount = (value: string): boolean => {
  const regExp = /^[-]?[0-9]+\.?[0-9]{0,2}$/
  return regExp.test(value)
}

/** Returns true for invalid value. Returns false when valid. */
export const validateEmail = (value: string): boolean => {
  if (value) {
    const filter =
      // eslint-disable-next-line no-useless-escape
      /^\s*[\w\-_]+(\.[\w\-_]+)*\@[\w\-_]+\.[\w\-_]+(\.[\w\-_]+)*\s*$/
    return (
      String(value).search(filter) === -1 ||
      value.length < 6 ||
      value.length > 50
    )
  }
  return true
}

export const validateUnappliedPayment = ({
  unappliedAmount,
  unappliedCash,
  paymentTotal
}: AnyObject): string => {
  if (unappliedAmount && unappliedAmount !== '') {
    if (!validAmount(unappliedAmount) || unappliedAmount <= 0)
      return invalidDecimalMessage
    if (unappliedAmount > paymentTotal - 1)
      return deficitInvoiceTotalErrorMessage
    if (unappliedAmount > unappliedCash) return exceedInvoiceTotalErrorMessage
  }
  return ''
}

export const validatePhoneNumber = (value: string): boolean => {
  if (!value) {
    return true
  }
  return !!(value && !/^\d{10}$/.test(value))
}

export const validateYear = (
  startMonth?: number,
  startYear?: number,
  endMonth?: number,
  endYear?: number
): string => {
  let errorMessage = ''
  if (startMonth?.toString() && startYear && endMonth?.toString() && endYear) {
    const currentDate = new Date()
    const startDate = new Date(startYear, startMonth)
    const endDate = new Date(endYear, endMonth)
    const monthDifference = differenceInMonths(endDate, startDate)
    if (
      differenceInDays(currentDate, startDate) < 0 ||
      differenceInDays(currentDate, endDate) < 0
    ) {
      errorMessage = exceedCurrentMonth
    } else if (differenceInYears(endDate, startDate) < 0) {
      errorMessage = invalidYearDuration
    } else if (monthDifference < 0) {
      errorMessage = monthDifferenceErrorMessage
    } else if (monthDifference > 12) {
      errorMessage = invalidMonthDuration
    }
  }

  return errorMessage
}

export const validateFaxNumber = (faxNumber = ''): boolean => {
  // eslint-disable-next-line no-useless-escape
  const regExpress = /^(?=(?:\D*\d){10,12}\D*$)[0-9 \-()\\\/]{1,16}$/
  return regExpress.test(faxNumber)
}

export const validateNoSpace = (value = ''): boolean => {
  const regex = /^\S+$/g
  return regex.test(value)
}

export const validateString = (value = ''): boolean => {
  const regExpress = /^[A-Za-z\s]+$/
  return regExpress.test(value)
}

export const validateStringNoSpace = (value = ''): boolean => {
  const regex = /^\S+$/g
  return validateString(value) && regex.test(value)
}

export const validateStringExists = (value = ''): boolean => {
  return value.trim().length > 0
}

export const validateUserId = (userId = ''): boolean => {
  // eslint-disable-next-line no-useless-escape
  const regex = /^[^@.](?=.*[a-zA-Z0-9_/-]).[A-Za-z0-9@._/-]*$/
  const { length: lengthOfUserId } = userId
  return regex.test(userId) && lengthOfUserId >= 6 && lengthOfUserId <= 50
}

export const hasSpaces = (value = '') => {
  return !validateNoSpace(value) && value.length > 0
}

// Names of Email Fields found in the application
const emailFields = ['email', 'emailAddress']

// Names of User ID Fields found in the application
const userIDFields = [
  'userId',
  'userID',
  'currentUserId',
  'reEnterUserId',
  'newUserId'
]

export const isEmailField = (name: string) => {
  return emailFields.includes(name)
}

export const isUserIDField = (name: string) => {
  return userIDFields.includes(name)
}

/** Returns true if there is no whitespace found in the input */
export const validateNoWhitespace = (name: string, value: string): boolean => {
  const needWhiteSpacesRestriction = [...userIDFields, ...emailFields]

  return !needWhiteSpacesRestriction.includes(name) || !hasSpaces(value)
}

interface ValidatePasswordResult {
  isUpperCaseEntered: boolean
  isLowerCaseEntered: boolean
  isNumbersEntered: boolean
  isLengthValidated: boolean
  invalidPassword: string
}

/**
 * Returns object describing the validation results for a given password string.
 *
 * @param value The password to validate.
 * @returns Object describing reason for validation success/failure. The
 * invalidPassword property in the returned object is an empty string when the
 * given password is valid.
 */
export const validatePassword = (value: string): ValidatePasswordResult => {
  const result: ValidatePasswordResult = {
    isUpperCaseEntered: false,
    isLowerCaseEntered: false,
    isNumbersEntered: false,
    isLengthValidated: false,
    invalidPassword: ''
  }

  const regExp = {
    length: /.{7,}/,
    upperCase: /(?=.*[A-Z])/,
    lowerCase: /(?=.*[a-z])/,
    numbers: /(?=.*[0-9]{2})/,
    maxLength: /.{15,}/,
    // less than and greater than (< >) special characters are invalid
    specialChar: /.*[<>]/
  }

  if (value && value.length > 0) {
    if (regExp.upperCase.test(value)) {
      result.isUpperCaseEntered = true
    }
    if (regExp.lowerCase.test(value)) {
      result.isLowerCaseEntered = true
    }
    if (regExp.numbers.test(value)) {
      result.isNumbersEntered = true
    }
    if (regExp.length.test(value) && value.length <= 15) {
      result.isLengthValidated = true
    }
    if (
      value.length > 15 ||
      regExp.specialChar.test(value) ||
      !validateNoSpace(value)
    ) {
      // The message must be assigned to a const to avoid a Fortify Scan issue
      const invalidErrorMessage = 'Invalid Password'
      result.invalidPassword = invalidErrorMessage
    }
  }
  return result
}

export const checkSpecialCharacters = (value = ''): boolean => {
  // eslint-disable-next-line no-useless-escape
  const specialChar = /[*|\":<>[\]{}`\\()';@&$]/
  return specialChar.test(value)
}

export const validateKeyPress = (
  event: React.KeyboardEvent<FormEvent>,
  type: string
): boolean | void => {
  const { key } = event
  let isValid = true
  if (type === 'amount') {
    // eslint-disable-next-line no-case-declarations
    const value = key !== '0' ? `${key}0` : key
    isValid = validAmount(value)
  }
  if (!isValid) {
    event.preventDefault()
  }
}

export const matchPatternList: AnyObject = {
  stringDigitWithHypen: /^$|^[a-z A-Z 0-9 -]+$/
}

export const validateLengthSpecialCharacters = (
  value: string,
  maxLength: number
): boolean => {
  if (value) {
    const { length: noOfCharacters } = value
    return noOfCharacters > maxLength || checkSpecialCharacters(value)
  }
  return false
}

export const validateName = (value = ''): boolean => {
  return (
    validateString(value) &&
    validateStringNoSpace(value) &&
    value.length > 1 &&
    value.length < 21
  )
}

export const validateBuyerName = (value = ''): boolean => {
  // auth buyers may only use letters (upper or lower case are okay)
  const regExpress = /^[a-z]+$/i
  return regExpress.test(value)
}

export const validateAddress = (value: string): boolean => {
  const regExp = /^$|^[a-z A-Z 0-9 -]+$/
  return regExp.test(value)
}

/**
 * Function to return a reason for failed validation when given field is invalid
 * or an empty string when the given field is valid.
 *
 * @param fieldName Name of the field to validate
 * @param value Value to check
 * @param isFieldValid Optional custom validator function
 * @param noOfCharacters Optional argument passed as second arg to isFieldValid
 * @returns Returns validation error message if validation fails. Returns '' if
 * given field is valid.
 *
 * @example
 * // returns ''
 * validateField('Phone Number', '8765674353', (value) => /^\d{10}$/.test(value))
 * @example
 * // returns 'Phone Number is required.'
 * validateField('Phone Number', '', (value) => /^\d{10}$/.test(value))
 * @example
 * // returns 'Invalid Phone Number.'
 * validateField('Phone Number', '87656743', (value) => /^\d{10}$/.test(value))
 */
export const validateField = (
  fieldName: string,
  value: string,
  isFieldValid?: (value: string, noOfCharacters?: number) => boolean,
  noOfCharacters?: number
): string => {
  if (!validateStringExists(value)) {
    return `${fieldName} ${isRequired}`
  }

  if (isFieldValid && isFieldValid(value, noOfCharacters)) {
    return `Invalid ${fieldName}.`
  }

  return ''
}

export const validateFieldByType = (
  fieldValue?: string,
  fieldType?: string
): boolean => {
  switch (fieldType) {
    case 'email':
      return !validateEmail(fieldValue)
    case 'phoneNumber':
      return !validatePhoneNumber(fieldValue)
    case 'userId':
      return validateUserId(fieldValue)
    case 'specialCharacters':
      return !checkSpecialCharacters(fieldValue)
    case 'stringExist':
      return validateStringExists(fieldValue)
    case 'string':
      return validateString(fieldValue)
    case 'zipCode':
      return isValidZipCode(fieldValue)
    case 'name':
      return validateName(fieldValue)
    case 'buyerName':
      return validateBuyerName(fieldValue)
    case 'address':
      return validateAddress(fieldValue)
    case 'accountNumber':
      /*
       * validating account number based on client specific criteria. assumes
       * that length validation applied separately
       */
      return accountNumberConfig.customValidateNumber?.(fieldValue) ?? true
    default:
      return true
  }
}

export const splitString = (value: string): string =>
  value
    .split(/(?=[A-Z])/)
    .map(name => `${name.slice(0, 1).toUpperCase()}${name.slice(1)}`)
    .join(' ')

const ALL_FIELDS_REQUIRED = 'all-fields-required'

export interface ValidateParams {
  /** name of the field to be validated */
  name: string
  /** value to validate */
  value: string
  /** overrides name parameter name parameter as the field name */
  type?: string
  minLength?: number
  maxLength?: number
  /** optional custom error message */
  customMessage?: string
  errorName?: string
  /**
   * optional numeric string for > constraint on numeric value.
   */
  minValue?: string
  /**
   * Default to ALL_FIELDS_REQUIRED to indicate that the caller has not been
   * updated for handling optional/required fields.
   */
  isRequiredField?: boolean | typeof ALL_FIELDS_REQUIRED
}

/**
 * Returns a validation error message. Returns empty string ('') when the value
 * is valid.
 */
export const validate = ({
  name,
  value,
  type,
  minLength,
  maxLength,
  customMessage,
  errorName,
  minValue,
  isRequiredField = ALL_FIELDS_REQUIRED
}: ValidateParams): string => {
  const fieldName = type || name

  const errorFieldName = errorName || splitString(name)
  const missingStringValue = !validateStringExists(value)
  const showRequiredError =
    missingStringValue &&
    (isRequiredField || isRequiredField === ALL_FIELDS_REQUIRED)

  if (showRequiredError) {
    return `${errorFieldName} ${isRequired}`
  }

  if (minLength || maxLength || minValue) {
    const { length } = value

    /**
     * Same guard for two cases:
     * 1. Check min or max length for text fields
     * 2. Check minValue for number fields
     * returns customMessage set with data-custom-message,
     * otherwise a generic invalid error message
     */
    if (
      length > maxLength ||
      length < minLength ||
      parseFloat(value) <= parseFloat(minValue)
    ) {
      return customMessage || `Invalid ${errorFieldName}.`
    }
  }

  if (!validateFieldByType(value, fieldName)) {
    return `Invalid ${errorFieldName}.`
  }

  return ''
}

export const hasNoChanges = (initialValue: string, newValue: string): boolean =>
  initialValue === newValue
